import { InfoIcon } from '@phosphor-icons/react';

import { Switch } from '@/components/common/switch';
import { Drawer } from '@/components/feedback/drawer';
import { RegionError } from '@/components/feedback/error-state';

import {
  NOTIFICATION_CATEGORY_COPY,
  NOTIFICATION_COPY,
  type NotificationCategory,
} from '../constants';
import { useNotificationSettings, useUpdateNotificationSetting } from '../hooks';

import styles from './notification-settings-drawer.module.scss';

export type NotificationSettingsDrawerProps = {
  open: boolean;
  onClose: () => void;
};

/**
 * Notification preferences (Figma 625:576366; save-failure toast 1065:570623) — US-113356.
 *
 * A drawer, not a page: the design opens it over whatever the grower was doing and dismisses it
 * with the shared `<CloseButton>`. The story describes a *screen* reached from Profile with a back
 * control, which is its mobile shape — the web design is what is built here.
 *
 * Every toggle auto-saves. There is no footer and no Save button, so `<Drawer>` is given no
 * primary action; each switch is its own mutation, optimistic with a revert on failure.
 */
export function NotificationSettingsDrawer({ open, onClose }: NotificationSettingsDrawerProps) {
  // Only fetched while open — an unopened drawer should not cost a request on every page.
  const {
    data: settings,
    isLoading,
    isError,
    refetch,
  } = useNotificationSettings({ enabled: open });
  const update = useUpdateNotificationSetting();

  const onToggle = (category: NotificationCategory, enabled: boolean) => {
    // Both outcomes are `<MutationToaster>`'s, from the mutation's `meta` — the green confirmation
    // names the category, and a save failure is warning-toned (it is not a 400) after the hook has
    // already reverted the optimistic write.
    update.mutate({ category, enabled });
  };

  return (
    // "Notification Preferences" is the AC's name for this surface (US-113356 § A) and the name
    // the profile menu uses to reach it; the design's "Notification Settings" was the odd one out.
    <Drawer open={open} onClose={onClose} title="Notification Preferences">
      <p className={styles.banner}>
        <InfoIcon size={16} weight="fill" className={styles.bannerIcon} aria-hidden />
        <span>{NOTIFICATION_COPY.ALERTS_ALWAYS_DELIVERED}</span>
      </p>

      {/*
        A failed fetch cannot render the toggles: a switch is a claim about what this grower chose,
        and defaulting it on (which is right *while loading*, below) would be a claim made from
        nothing. So the list is replaced by the shared notice, and the retry re-requests this one
        query — the rest of the drawer is static copy and stays.
      */}
      {isError ? (
        <RegionError
          message={NOTIFICATION_COPY.SETTINGS_LOAD_FAILED}
          onRetry={() => void refetch()}
        />
      ) : (
        <ul className={styles.list}>
          {NOTIFICATION_CATEGORY_COPY.map(({ category, title, description }) => (
            <li key={category} className={styles.item}>
              <div className={styles.copy}>
                <p className={styles.title}>{title}</p>
                <p className={styles.description}>{description}</p>
              </div>
              <Switch
                size="lg"
                // Defaults to on: a category the server has not spoken about is enabled, so a
                // still-loading drawer never shows a grower's notifications as switched off.
                checked={settings?.[category] ?? true}
                // Disabled while a save is in flight, not only while loading. The optimistic write
                // snapshots the cache, so a second toggle started before the first settles would
                // snapshot the *unconfirmed* value and the two reverts could land out of order,
                // leaving the switch showing one thing and the server holding another.
                disabled={isLoading || update.isPending}
                aria-label={title}
                onChange={(checked) => onToggle(category, checked)}
              />
            </li>
          ))}
        </ul>
      )}

      <p className={styles.footer}>{NOTIFICATION_COPY.SAFETY_CRITICAL_FOOTER}</p>
    </Drawer>
  );
}
